import React, { useState } from 'react';

const SectionHeader = ({ title }) => (
  <div style={{
    backgroundColor: '#1a1a2e', color: 'white', padding: '8px 12px',
    borderRadius: '4px', marginTop: '16px', marginBottom: '8px',
    fontSize: '13px', fontWeight: 'bold'
  }}>
    {title}
  </div>
);

// Adds thousands separators while typing (e.g. 1450000 -> 1,450,000).
// Preserves a trailing decimal point so mid-typing values like "1450."
// or "1450.5" aren't mangled before the next digit is entered.
function formatWithCommas(raw) {
  if (raw === undefined || raw === null) return '';
  const str = String(raw);
  const neg = str.startsWith('-') ? '-' : '';
  const body = neg ? str.slice(1) : str;
  const [intPart, ...decParts] = body.split('.');
  const cleanInt = intPart.replace(/\D/g, '');
  const withCommas = cleanInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (decParts.length > 0) {
    return neg + withCommas + '.' + decParts.join('').replace(/\D/g, '');
  }
  return neg + withCommas + (body.endsWith('.') ? '.' : '');
}

// Strips commas back out before the value is stored, so all downstream
// math, Supabase saves, and CSV export keep working on plain numeric strings.
function stripCommas(formatted) {
  if (formatted === undefined || formatted === null) return '';
  const str = String(formatted);
  const neg = str.startsWith('-') ? '-' : '';
  const body = (neg ? str.slice(1) : str).replace(/,/g, '');
  const firstDot = body.indexOf('.');
  if (firstDot === -1) return neg + body.replace(/[^\d]/g, '');
  const intPart = body.slice(0, firstDot).replace(/[^\d]/g, '');
  const decPart = body.slice(firstDot + 1).replace(/[^\d]/g, '');
  return neg + intPart + '.' + decPart;
}

const InputRow = ({ label, field, suffix, inputs, update }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
    <label style={{ fontSize: '13px', color: '#555', flex: 1, marginRight: '8px' }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
      {!suffix && <span style={{ fontSize: '13px', color: '#888' }}>$</span>}
      <input
        type="text" inputMode="decimal"
        value={formatWithCommas(inputs[field] || '')}
        onChange={e => update(field, stripCommas(e.target.value))}
        style={{ width: '90px', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', textAlign: 'right' }}
      />
      {suffix && <span style={{ fontSize: '13px', color: '#888' }}>{suffix}</span>}
    </div>
  </div>
);

const TextInputRow = ({ label, field, inputs, update, placeholder }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
    <label style={{ fontSize: '13px', color: '#555', flex: 1, marginRight: '8px' }}>{label}</label>
    <input
      type="text"
      value={inputs[field] || ''}
      onChange={e => update(field, e.target.value)}
      placeholder={placeholder || ''}
      style={{ width: '180px', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }}
    />
  </div>
);

const ReadOnlyRow = ({ label, value, bold, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
    <span style={{ fontSize: '13px', color: '#555', fontWeight: bold ? 'bold' : 'normal' }}>{label}</span>
    <span style={{ fontSize: '13px', fontWeight: bold ? 'bold' : 'normal', color: color || '#333' }}>{value}</span>
  </div>
);

const DualCarryRow = ({ label, monthlyField, annualField, inputs, update }) => {
  const handleMonthly = (val) => {
    const clean = stripCommas(val);
    update(monthlyField, clean);
    const n = parseFloat(clean);
    if (!isNaN(n) && clean !== '') update(annualField, (n * 12).toFixed(2));
    else update(annualField, '');
  };
  const handleAnnual = (val) => {
    const clean = stripCommas(val);
    update(annualField, clean);
    const n = parseFloat(clean);
    if (!isNaN(n) && clean !== '') update(monthlyField, (n / 12).toFixed(2));
    else update(monthlyField, '');
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0', gap: '8px' }}>
      <label style={{ fontSize: '13px', color: '#555' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
        <span style={{ fontSize: '11px', color: '#aaa' }}>$</span>
        <input
          type="text" inputMode="decimal"
          value={formatWithCommas(inputs[monthlyField] || '')}
          onChange={e => handleMonthly(e.target.value)}
          style={{ width: '100%', padding: '4px 6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', textAlign: 'right', backgroundColor: '#fffbeb' }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
        <span style={{ fontSize: '11px', color: '#aaa' }}>$</span>
        <input
          type="text" inputMode="decimal"
          value={formatWithCommas(inputs[annualField] || '')}
          onChange={e => handleAnnual(e.target.value)}
          style={{ width: '100%', padding: '4px 6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', textAlign: 'right', backgroundColor: '#f0f8ff' }}
        />
      </div>
    </div>
  );
};

// Load Google Places script once
let googlePlacesLoaded = false;
function loadGooglePlaces(cb) {
  if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.PlaceAutocompleteElement) {
    cb();
    return;
  }
  if (googlePlacesLoaded) {
    const interval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.PlaceAutocompleteElement) {
        clearInterval(interval);
        cb();
      }
    }, 100);
    return;
  }
  googlePlacesLoaded = true;
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}&libraries=places&loading=async`;
  script.async = true;
  script.onload = () => {
    const interval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.PlaceAutocompleteElement) {
        clearInterval(interval);
        cb();
      }
    }, 100);
  };
  document.head.appendChild(script);
}

const AddressAutocomplete = ({ onPlaceSelected }) => {
  const containerRef = React.useRef(null);
  const elementRef = React.useRef(null);
  const onPlaceSelectedRef = React.useRef(onPlaceSelected);
  onPlaceSelectedRef.current = onPlaceSelected;

  React.useEffect(() => {
    loadGooglePlaces(() => {
      if (!containerRef.current || elementRef.current) return;

      const placeAutocomplete = new window.google.maps.places.PlaceAutocompleteElement({
        types: ['address'],
        componentRestrictions: { country: 'us' },
      });

      placeAutocomplete.style.width = '100%';
      containerRef.current.appendChild(placeAutocomplete);
      elementRef.current = placeAutocomplete;

      const handleSelect = async (e) => {
        try {
          const prediction = e.placePrediction || (e.detail && e.detail.placePrediction);
          if (!prediction) {
            console.log('No prediction in event', e);
            return;
          }
          const place = prediction.toPlace();
          await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });

          const get = (type) => {
            const c = place.addressComponents && place.addressComponents.find(c => c.types.includes(type));
            return c ? c.longText : '';
          };
          const getShort = (type) => {
            const c = place.addressComponents && place.addressComponents.find(c => c.types.includes(type));
            return c ? c.shortText : '';
          };

          const streetNumber = get('street_number');
          const route = get('route');
          const address = streetNumber && route ? `${streetNumber} ${route}` : place.formattedAddress || '';

          onPlaceSelectedRef.current({
            propAddress: address,
            propCity: get('locality') || get('sublocality') || get('neighborhood'),
            propState: getShort('administrative_area_level_1'),
            propZip: get('postal_code'),
            propCounty: get('administrative_area_level_2').replace(' County', ''),
          });
        } catch (err) {
          console.error('Error handling place select:', err);
        }
      };

      placeAutocomplete.addEventListener('gmp-placeselect', handleSelect);
      placeAutocomplete.addEventListener('gmp-select', handleSelect);
    });
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <div ref={containerRef} style={{ width: '100%' }} />
    </div>
  );
};

const PropertyInfoSection = ({ inputs, update }) => {
  const handlePlaceSelected = React.useCallback((fields) => {
    Object.entries(fields).forEach(([field, value]) => update(field, value));
  }, [update]);

  return (
    <div>
      <SectionHeader title="Property Information" />
      <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
        <label style={{ fontSize: '13px', color: '#555', display: 'block', marginBottom: '6px' }}>
          Property Address
          <span style={{ marginLeft: '6px', fontSize: '11px', color: '#c9a84c', fontStyle: 'italic' }}>
            — type to auto-fill city, state, zip & county
          </span>
        </label>
        <AddressAutocomplete onPlaceSelected={handlePlaceSelected} />
      </div>
      <TextInputRow label="Street Address" field="propAddress" inputs={inputs} update={update} placeholder="Auto-filled" />
      <TextInputRow label="City" field="propCity" inputs={inputs} update={update} placeholder="Auto-filled" />
      <TextInputRow label="State" field="propState" inputs={inputs} update={update} placeholder="Auto-filled" />
      <TextInputRow label="Zip Code" field="propZip" inputs={inputs} update={update} placeholder="Auto-filled" />
      <TextInputRow label="County" field="propCounty" inputs={inputs} update={update} placeholder="Auto-filled" />
      <TextInputRow label="Parcel / APN #" field="propParcel" inputs={inputs} update={update} placeholder="00-0000-000-0000" />
      <TextInputRow label="Property Type" field="propType" inputs={inputs} update={update} placeholder="SFR / Condo / Vacant Lot" />
      <TextInputRow label="Auction / Sale Date" field="propAuctionDate" inputs={inputs} update={update} placeholder="MM/DD/YYYY" />
      <TextInputRow label="Notes" field="propNotes" inputs={inputs} update={update} placeholder="Any additional notes..." />
      <TextInputRow label="Zillow / Redfin URL" field="propListingUrl" inputs={inputs} update={update} placeholder="Paste listing link here..." />
    </div>
  );
};

const CarryingCostsSection = ({ inputs, update, monthlyPayment }) => {
  const n = (val) => parseFloat(val) || 0;
  const fmt = (num) => (num < 0 ? '-$' : '$') + Math.abs(Math.round(num)).toLocaleString();
  const rows = [
    { label: 'Property Taxes',       mo: 'carryTaxesMo',      yr: 'carryTaxes' },
    { label: 'Homeowners Insurance',  mo: 'carryInsuranceMo',  yr: 'carryInsurance' },
    { label: 'HOA Fee',              mo: 'carryHOAMo',         yr: 'carryHOA' },
    { label: 'Utilities',            mo: 'carryUtilitiesMo',   yr: 'carryUtilities' },
    { label: 'Maintenance',          mo: 'carryMaintenanceMo', yr: 'carryMaintenance' },
    { label: 'Other',                mo: 'carryOtherMo',       yr: 'carryOther' },
  ];
  const expensesAnnual = rows.reduce((sum, r) => sum + n(inputs[r.yr]), 0);
  const expensesMonthly = expensesAnnual / 12;
  const mp = monthlyPayment || 0;
  const totalMonthly = expensesMonthly + mp;
  const totalAnnual = totalMonthly * 12;

  return (
    <div>
      <SectionHeader title="Carrying Costs (Total Cost to Own)" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px', gap: '8px', padding: '4px 0 6px', borderBottom: '2px solid #1a1a2e' }}>
        <span style={{ fontSize: '11px', color: '#888', fontWeight: 'bold' }}>EXPENSE</span>
        <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#92400e', backgroundColor: '#fffbeb', borderRadius: '4px', padding: '3px' }}>MONTHLY</div>
        <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#1565c0', backgroundColor: '#f0f8ff', borderRadius: '4px', padding: '3px' }}>ANNUAL</div>
      </div>
      {rows.map(r => (
        <DualCarryRow key={r.label} label={r.label} monthlyField={r.mo} annualField={r.yr} inputs={inputs} update={update} />
      ))}
      {mp > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0', gap: '8px', backgroundColor: '#f8f8f8' }}>
          <label style={{ fontSize: '13px', color: '#555', fontStyle: 'italic' }}>Mortgage / Financing</label>
          <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 'bold', color: '#1a1a2e', paddingRight: '6px' }}>{fmt(mp)}</div>
          <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 'bold', color: '#1a1a2e', paddingRight: '6px' }}>{fmt(mp * 12)}</div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px', gap: '4px', marginTop: '10px' }}>
        <div style={{ backgroundColor: '#1a1a2e', padding: '8px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: 'white', fontWeight: 'bold' }}>Total Cost to Own</span>
        </div>
        <div style={{ backgroundColor: '#c9a84c', padding: '8px 6px', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)' }}>/ month</div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>{fmt(totalMonthly)}</div>
        </div>
        <div style={{ backgroundColor: '#1a1a2e', padding: '8px 6px', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)' }}>/ year</div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>{fmt(totalAnnual)}</div>
        </div>
      </div>
    </div>
  );
};

function calcFinancing(purchasePrice, downPct, interestRate, termMonths, loanType) {
  const price = parseFloat(purchasePrice) || 0;
  const down = parseFloat(downPct) || 0;
  const rate = parseFloat(interestRate) || 0;
  const term = parseFloat(termMonths) || 0;
  const downAmt = price * (down / 100);
  const loanAmt = price - downAmt;
  const monthlyRate = rate / 100 / 12;
  let monthlyPayment = 0;
  if (loanType === 'interestOnly') {
    monthlyPayment = loanAmt * monthlyRate;
  } else if (monthlyRate > 0 && term > 0) {
    monthlyPayment = loanAmt * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
  }
  return { downAmt, loanAmt, monthlyPayment };
}

const LoanTypeToggle = ({ value, onChange }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
    <label style={{ fontSize: '13px', color: '#555' }}>Loan Type</label>
    <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid #ddd' }}>
      {[['amortizing', 'Amortizing'], ['interestOnly', 'Interest Only']].map(([val, label]) => (
        <button key={val} onClick={() => onChange(val)} style={{
          padding: '4px 10px', border: 'none', cursor: 'pointer', fontSize: '12px',
          backgroundColor: value === val ? '#1a1a2e' : 'white',
          color: value === val ? 'white' : '#555',
          fontWeight: value === val ? 'bold' : 'normal',
        }}>{label}</button>
      ))}
    </div>
  </div>
);

const NextTabButton = ({ label, onClick }) => (
  <button onClick={onClick} style={{
    width: '100%', padding: '11px', marginTop: '20px',
    backgroundColor: 'white', color: '#1a1a2e',
    border: '2px solid #1a1a2e', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
  }}>
    {label} <span style={{ fontSize: '16px' }}>→</span>
  </button>
);

const SaveButton = ({ onSave, saved }) => (
  <button onClick={onSave} style={{
    width: '100%', padding: '12px', marginTop: '16px',
    backgroundColor: saved ? '#2d6a4f' : '#1a1a2e',
    color: 'white', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
  }}>
    {saved ? '✓ Saved to Due Diligence' : 'Save to Due Diligence'}
  </button>
);


// â”€â”€â”€ CLOSING COST ESTIMATOR MODAL â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function ClosingCostModal({ purchasePrice, state, onApply, onClose }) {
  const n = (v) => parseFloat(v) || 0;
  const fmt = (num) => (num < 0 ? '-$' : '$') + Math.abs(Math.round(num)).toLocaleString();
  const price = n(purchasePrice);
  const [overrides, setOverrides] = React.useState({});

  const auto = {
    escrowFee: price > 0 ? Math.min(Math.max(price * 0.003, 1500), 6000) : 0,
    titleInsurance: price * 0.004,
    lenderFees: price * 0.01,
    appraisal: price > 0 ? 800 : 0,
    recording: price > 0 ? 150 : 0,
    transferTax: state === 'CA' ? price * 0.0011 : state === 'NY' ? price * 0.004 : price * 0.001,
  };

  const getVal = (field) => overrides[field] !== undefined && overrides[field] !== '' ? n(overrides[field]) : auto[field];
  const total = Object.keys(auto).reduce((sum, k) => sum + getVal(k), 0);

  const rows = [
    { label: 'Escrow Fee', field: 'escrowFee', note: '~0.3% of price' },
    { label: 'Title Insurance', field: 'titleInsurance', note: '~0.4% of price' },
    { label: 'Lender / Origination Fees', field: 'lenderFees', note: '~1% of price' },
    { label: 'Appraisal', field: 'appraisal' },
    { label: 'Recording Fees', field: 'recording' },
    { label: 'Transfer Tax', field: 'transferTax', note: state === 'CA' ? 'CA: $1.10/$1,000' : '$1/$1,000 est.' },
  ];
  // Note: Home Inspection and Misc are separate fields in the calculator — not included here to avoid double-counting

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ backgroundColor: '#1a1a2e', borderRadius: '12px 12px 0 0', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: 'white', fontWeight: 'bold', fontSize: '15px' }}>Closing Cost Estimator</div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '2px' }}>Based on {fmt(price)} purchase price</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', padding: '0 4px' }}>✕</button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          <p style={{ fontSize: '12px', color: '#888', margin: '0 0 12px' }}>
            Auto-estimated from purchase price. Override any line item — yellow = manually set.
          </p>

          {rows.map(row => {
            const isOverridden = overrides[row.field] !== undefined && overrides[row.field] !== '';
            const displayVal = isOverridden ? overrides[row.field] : Math.round(auto[row.field]).toString();
            return (
              <div key={row.field} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f0f0f0', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', color: '#555' }}>{row.label}</div>
                  {row.note && <div style={{ fontSize: '11px', color: '#aaa' }}>{row.note}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                  {isOverridden && (
                    <button onClick={() => setOverrides(prev => { const next = {...prev}; delete next[row.field]; return next; })}
                      style={{ fontSize: '10px', color: '#c9a84c', background: 'none', border: '1px solid #c9a84c', borderRadius: '3px', padding: '1px 5px', cursor: 'pointer' }}>
                      auto
                    </button>
                  )}
                  <span style={{ fontSize: '13px', color: '#888' }}>$</span>
                  <input
                    type="text" inputMode="decimal"
                    value={formatWithCommas(displayVal)}
                    onChange={e => setOverrides(prev => ({ ...prev, [row.field]: stripCommas(e.target.value) }))}
                    style={{ width: '85px', padding: '4px 8px', fontSize: '14px', textAlign: 'right', border: '1px solid ' + (isOverridden ? '#c9a84c' : '#ddd'), borderRadius: '4px', backgroundColor: isOverridden ? '#fffbeb' : 'white' }}
                  />
                </div>
              </div>
            );
          })}

          {/* Total */}
          <div style={{ backgroundColor: '#1a1a2e', borderRadius: '8px', padding: '14px 16px', marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>Total Estimated Closing Costs</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '2px' }}>
                {price > 0 ? ((total / price) * 100).toFixed(1) + '% of purchase price' : ''}
              </div>
            </div>
            <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'white' }}>{fmt(total)}</div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '16px' }}>
            <button onClick={onClose}
              style={{ padding: '12px', backgroundColor: 'white', color: '#555', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
              Cancel
            </button>
            <button onClick={() => { onApply(Math.round(total).toString()); onClose(); }}
              style={{ padding: '12px', backgroundColor: '#2d6a4f', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
              Apply {fmt(total)} â†’
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- FIX & FLIP CALCULATOR ---
function FixFlipCalculator({ onSaveDeal }) {
  const [inputs, setInputs] = useState({
    propAddress: '', propCity: '', propState: '', propZip: '',
    propCounty: '', propParcel: '', propType: '', propAuctionDate: '', propNotes: '',
    carryTaxesMo: '', carryTaxes: '', carryInsuranceMo: '', carryInsurance: '', carryHOAMo: '', carryHOA: '', carryUtilitiesMo: '', carryUtilities: '', carryMaintenanceMo: '', carryMaintenance: '', carryOtherMo: '', carryOther: '',
    purchasePrice: '', closingCostsBuy: '', inspectionFee: '', miscBuy: '',
    totalRehab: '', contingency: '',
    downPct: '', interestRate: '', termMonths: '', loanType: 'amortizing',
    arv: '', agentCommission: '', sellerClosing: '',
    holdingMonths: '', monthlyHolding: '', otherSaleExp: '',
    monthlyRent: '', vacancyRate: '', mgmtFee: '',
    annualTax: '', annualInsurance: '', annualMaintenance: '',
  });
  const [activeTab, setActiveTab] = useState('acquisition');
  const [saved, setSaved] = useState(false);
  const [showClosingModal, setShowClosingModal] = useState(false);
  const update = (field, value) => { setInputs(prev => ({ ...prev, [field]: value })); setSaved(false); };
  const n = (val) => parseFloat(val) || 0;

  const totalAcq = n(inputs.purchasePrice) + n(inputs.closingCostsBuy) + n(inputs.inspectionFee) + n(inputs.miscBuy);
  const rehabWithContingency = n(inputs.totalRehab) * (1 + n(inputs.contingency) / 100);
  const totalAllIn = totalAcq + rehabWithContingency;
  const { downAmt, loanAmt, monthlyPayment } = calcFinancing(inputs.purchasePrice, inputs.downPct, inputs.interestRate, inputs.termMonths, inputs.loanType);
  const carryExpensesMonthly = (n(inputs.carryTaxes) + n(inputs.carryInsurance) + n(inputs.carryHOA) + n(inputs.carryUtilities) + n(inputs.carryMaintenance) + n(inputs.carryOther)) / 12;
  const autoMonthlyHolding = carryExpensesMonthly + monthlyPayment;
  const effectiveMonthlyHolding = n(inputs.monthlyHolding) > 0 ? n(inputs.monthlyHolding) : autoMonthlyHolding;
  const agentDollar = n(inputs.arv) * (n(inputs.agentCommission) / 100);
  const closingDollar = n(inputs.arv) * (n(inputs.sellerClosing) / 100);
  const holdingTotal = n(inputs.holdingMonths) * effectiveMonthlyHolding;
  const totalSaleExp = agentDollar + closingDollar + holdingTotal + n(inputs.otherSaleExp);
  const netProceeds = n(inputs.arv) - totalSaleExp;
  const netProfit = netProceeds - totalAllIn;
  // ROI on total all-in (includes full purchase price)
  const roiSale = totalAllIn > 0 ? (netProfit / totalAllIn) * 100 : 0;
  const cocSale = roiSale; // kept for top summary tiles
  // True CoC = profit Ã· actual cash out of pocket (down + closing + rehab)
  const cashOutOfPocket = downAmt + n(inputs.closingCostsBuy) + n(inputs.inspectionFee) + n(inputs.miscBuy) + rehabWithContingency;
  const trueCocSale = cashOutOfPocket > 0 ? (netProfit / cashOutOfPocket) * 100 : 0;
  const grossRent = n(inputs.monthlyRent) * 12;
  const vacancy = grossRent * (n(inputs.vacancyRate) / 100);
  const mgmtDollar = grossRent * (n(inputs.mgmtFee) / 100);
  const effectiveAnnualTax = n(inputs.annualTax) > 0 ? n(inputs.annualTax) : n(inputs.carryTaxes);
  const effectiveAnnualIns = n(inputs.annualInsurance) > 0 ? n(inputs.annualInsurance) : n(inputs.carryInsurance);
  const effectiveAnnualMaint = n(inputs.annualMaintenance) > 0 ? n(inputs.annualMaintenance) : n(inputs.carryMaintenance);
  const totalRentExp = mgmtDollar + vacancy + effectiveAnnualTax + effectiveAnnualIns + effectiveAnnualMaint + (monthlyPayment * 12);
  const noi = grossRent - totalRentExp;
  const cocRent = cashOutOfPocket > 0 ? (noi / cashOutOfPocket) * 100 : 0;
  const roiRent = totalAllIn > 0 ? (noi / totalAllIn) * 100 : 0;

  const fmt = (num) => (num < 0 ? '-$' : '$') + Math.abs(Math.round(num)).toLocaleString();
  const fmtPct = (num) => num.toFixed(1) + '%';
  const scoreColor = (pct, min) => pct >= min ? '#2d6a4f' : pct >= min * 0.75 ? '#c9a84c' : '#c0392b';
  const tabs = ['acquisition', 'sale', 'rental', 'scorecard'];
  const tabLabels = { acquisition: 'Acquisition Target', sale: 'Sale', rental: 'Rental', scorecard: 'Score' };

  const handleSave = () => {
    onSaveDeal({ type: 'Fix & Flip', address: inputs.propAddress ? `${inputs.propAddress}${inputs.propCity ? ', '+inputs.propCity : ''}${inputs.propState ? ', '+inputs.propState : ''}` : '', inputs, metrics: { totalAllIn, netProfit, cocSale, noi, cocRent, monthlyPayment, downAmt, loanAmt } });
    setSaved(true);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
        {[
          { label: 'All-In Cost', value: fmt(totalAllIn) },
          { label: 'Net Profit (Flip)', value: fmt(netProfit), color: netProfit >= 0 ? '#2d6a4f' : '#c0392b' },
          { label: 'CoC Return (Sale)', value: fmtPct(trueCocSale), bg: scoreColor(trueCocSale, 20) },
          { label: 'CoC Return (Rental)', value: fmtPct(cocRent), bg: scoreColor(cocRent, 14) },
        ].map((item, i) => (
          <div key={i} style={{ backgroundColor: item.bg || '#f8f8f8', padding: '10px 12px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '11px', color: item.bg ? 'rgba(255,255,255,0.8)' : '#888' }}>{item.label}</span>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: item.bg ? 'white' : (item.color || '#333') }}>{item.value}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex: 1, padding: '8px 4px', borderRadius: '6px', border: 'none',
            backgroundColor: activeTab === tab ? '#1a1a2e' : '#f0f0f0',
            color: activeTab === tab ? 'white' : '#333',
            cursor: 'pointer', fontSize: '11px', fontWeight: activeTab === tab ? 'bold' : 'normal'
          }}>{tabLabels[tab]}</button>
        ))}
      </div>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {activeTab === 'acquisition' && (
          <div>
            <PropertyInfoSection inputs={inputs} update={update} />
            <SectionHeader title="Purchase Costs" />
            <InputRow label="Purchase Price" field="purchasePrice" inputs={inputs} update={update} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
              <label style={{ fontSize: '13px', color: '#555', flex: 1 }}>Closing Costs (Buy)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <button onClick={() => setShowClosingModal(true)} style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#c9a84c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Estimate →</button>
                <span style={{ fontSize: '13px', color: '#888' }}>$</span>
                <input type="text" inputMode="decimal" value={formatWithCommas(inputs.closingCostsBuy || '')} onChange={e => update('closingCostsBuy', stripCommas(e.target.value))} style={{ width: '90px', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', textAlign: 'right' }} />
              </div>
            </div>
            {showClosingModal && <ClosingCostModal purchasePrice={inputs.purchasePrice} state={inputs.propState || 'CA'} onApply={(val) => update('closingCostsBuy', val)} onClose={() => setShowClosingModal(false)} />}
            <InputRow label="Inspection Fee" field="inspectionFee" inputs={inputs} update={update} />
            <InputRow label="Misc Costs" field="miscBuy" inputs={inputs} update={update} />
            <ReadOnlyRow label="Total Acquisition" value={fmt(totalAcq)} bold />
            <SectionHeader title="Rehab / CapEx" />
            <InputRow label="Total Rehab Estimate" field="totalRehab" inputs={inputs} update={update} />
            <InputRow label="Contingency Buffer" field="contingency" suffix="%" inputs={inputs} update={update} />
            <ReadOnlyRow label="Rehab with Contingency" value={fmt(rehabWithContingency)} />
            <div style={{ backgroundColor: '#1a1a2e', color: 'white', padding: '12px', borderRadius: '6px', marginTop: '12px', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
              <span>Total All-In Cost</span><span>{fmt(totalAllIn)}</span>
            </div>
            <SectionHeader title="Financing" />
            <InputRow label="Down Payment" field="downPct" suffix="%" inputs={inputs} update={update} />
            <LoanTypeToggle value={inputs.loanType} onChange={val => update('loanType', val)} />
            <InputRow label="Interest Rate" field="interestRate" suffix="%" inputs={inputs} update={update} />
            {inputs.loanType === 'amortizing' && <InputRow label="Term" field="termMonths" suffix="mo" inputs={inputs} update={update} />}
            <ReadOnlyRow label="Down Payment Amount" value={fmt(downAmt)} />
            <ReadOnlyRow label="Loan Amount" value={fmt(loanAmt)} />
            <ReadOnlyRow label="Monthly Payment" value={fmt(monthlyPayment)} bold color="#1a1a2e" />
            <CarryingCostsSection inputs={inputs} update={update} monthlyPayment={monthlyPayment} />
            <NextTabButton label="Next: Sale Scenario" onClick={() => setActiveTab('sale')} />
          </div>
        )}
        {activeTab === 'sale' && (
          <div>
            <SectionHeader title="Sale Inputs" />
            <InputRow label="After Repair Value (ARV)" field="arv" inputs={inputs} update={update} />
            <InputRow label="Agent Commission" field="agentCommission" suffix="%" inputs={inputs} update={update} />
            <InputRow label="Seller Closing Costs" field="sellerClosing" suffix="%" inputs={inputs} update={update} />
            <InputRow label="Holding Period" field="holdingMonths" suffix="mo" inputs={inputs} update={update} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
              <label style={{ fontSize: '13px', color: '#555', flex: 1 }}>Monthly Holding Cost</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {n(inputs.monthlyHolding) === 0 && autoMonthlyHolding > 0 && (
                  <span style={{ fontSize: '11px', color: '#c9a84c', fontStyle: 'italic' }}>auto from carry costs</span>
                )}
                <span style={{ fontSize: '13px', color: '#888' }}>$</span>
                <input
                  type="text" inputMode="decimal"
                  value={formatWithCommas(inputs.monthlyHolding || '')}
                  onChange={e => update('monthlyHolding', stripCommas(e.target.value))}
                  placeholder={autoMonthlyHolding > 0 ? Math.round(autoMonthlyHolding).toLocaleString() : ''}
                  style={{ width: '90px', padding: '4px 8px', border: '1px solid ' + (n(inputs.monthlyHolding) === 0 && autoMonthlyHolding > 0 ? '#c9a84c' : '#ddd'), borderRadius: '4px', fontSize: '16px', textAlign: 'right', backgroundColor: n(inputs.monthlyHolding) === 0 && autoMonthlyHolding > 0 ? '#fffbeb' : 'white' }}
                />
              </div>
            </div>
            <InputRow label="Other Sale Expenses" field="otherSaleExp" inputs={inputs} update={update} />

            <SectionHeader title="Sale P&L Walkdown" />
            {[
              { label: 'Sale Price (ARV)', value: fmt(n(inputs.arv)), positive: true },
              { label: '  − Agent Commission (' + n(inputs.agentCommission) + '%)', value: '(' + fmt(agentDollar) + ')', red: true },
              { label: '  − Seller Closing Costs (' + n(inputs.sellerClosing) + '%)', value: '(' + fmt(closingDollar) + ')', red: true },
              { label: '  − Holding Costs (' + n(inputs.holdingMonths) + ' mo × ' + fmt(effectiveMonthlyHolding) + ')', value: '(' + fmt(holdingTotal) + ')', red: true },
              { label: '  − Other Sale Expenses', value: '(' + fmt(n(inputs.otherSaleExp)) + ')', red: true },
              { label: 'Net Sale Proceeds', value: fmt(netProceeds), bold: true, divider: true },
              { label: '  − Purchase Price', value: '(' + fmt(n(inputs.purchasePrice)) + ')', red: true },
              { label: '  − Closing Costs (Buy)', value: '(' + fmt(n(inputs.closingCostsBuy)) + ')', red: true },
              { label: '  − Inspection & Misc', value: '(' + fmt(n(inputs.inspectionFee) + n(inputs.miscBuy)) + ')', red: true },
              { label: '  − Rehab (w/ contingency)', value: '(' + fmt(rehabWithContingency) + ')', red: true },
              { label: 'Net Profit', value: fmt(netProfit), bold: true, divider: true, color: netProfit >= 0 ? '#2d6a4f' : '#c0392b' },
            ].map((row, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: row.divider ? '2px solid #1a1a2e' : '1px solid #f5f5f5', marginTop: row.divider ? '4px' : '0' }}>
                <span style={{ fontSize: '13px', color: row.bold ? '#1a1a2e' : '#555', fontWeight: row.bold ? 'bold' : 'normal' }}>{row.label}</span>
                <span style={{ fontSize: '13px', fontWeight: row.bold ? 'bold' : 'normal', color: row.color || (row.red ? '#c0392b' : row.positive ? '#2d6a4f' : '#333') }}>{row.value}</span>
              </div>
            ))}
            <NextTabButton label="Next: Rental Analysis" onClick={() => setActiveTab('rental')} />
          </div>
        )}
        {activeTab === 'rental' && (
          <div>
            <SectionHeader title="Buy and Hold Scenario" />
            <InputRow label="Monthly Gross Rent" field="monthlyRent" inputs={inputs} update={update} />
            <InputRow label="Vacancy Rate" field="vacancyRate" suffix="%" inputs={inputs} update={update} />
            <InputRow label="Mgmt Fee" field="mgmtFee" suffix="%" inputs={inputs} update={update} />
            {[
              { label: 'Annual Property Tax', field: 'annualTax', carry: 'carryTaxes' },
              { label: 'Annual Insurance', field: 'annualInsurance', carry: 'carryInsurance' },
              { label: 'Annual Maintenance', field: 'annualMaintenance', carry: 'carryMaintenance' },
            ].map(({ label, field, carry }) => {
              const autoVal = n(inputs[carry]);
              const isAuto = !inputs[field] && autoVal > 0;
              return (
                <div key={field} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '13px', color: '#555' }}>{label}</label>
                    {isAuto && <span style={{ fontSize: '11px', color: '#2d6a4f', marginLeft: '6px', fontStyle: 'italic' }}>auto from carry costs</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    {inputs[field] && autoVal > 0 && (
                      <button onClick={() => update(field, '')} style={{ fontSize: '10px', color: '#c9a84c', background: 'none', border: '1px solid #c9a84c', borderRadius: '3px', padding: '1px 5px', cursor: 'pointer' }}>auto</button>
                    )}
                    <span style={{ fontSize: '13px', color: '#888' }}>$</span>
                    <input type="text" inputMode="decimal"
                      value={formatWithCommas(inputs[field] || '')}
                      placeholder={autoVal > 0 ? Math.round(autoVal).toLocaleString() : '0'}
                      onChange={e => update(field, stripCommas(e.target.value))}
                      style={{ width: '90px', padding: '4px 8px', border: '1px solid ' + (inputs[field] ? '#c9a84c' : '#ddd'), borderRadius: '4px', fontSize: '16px', textAlign: 'right', backgroundColor: inputs[field] ? '#fffbeb' : 'white' }} />
                  </div>
                </div>
              );
            })}
            <ReadOnlyRow label={`Monthly Financing (${inputs.loanType === 'interestOnly' ? 'I/O' : 'P&I'})`} value={fmt(monthlyPayment)} />
            <SectionHeader title="Rental P&L Walkdown (Annual)" />
            {[
              { label: 'Gross Rental Income', value: fmt(grossRent), positive: true },
              { label: '  − Vacancy (' + n(inputs.vacancyRate) + '%)', value: '(' + fmt(grossRent * n(inputs.vacancyRate) / 100) + ')', red: true },
              { label: 'Effective Gross Income', value: fmt(grossRent * (1 - n(inputs.vacancyRate)/100)), bold: true, divider: true },
              { label: '  − Mgmt Fee (' + n(inputs.mgmtFee) + '%)', value: '(' + fmt(grossRent * (1 - n(inputs.vacancyRate)/100) * n(inputs.mgmtFee)/100) + ')', red: true },
              { label: '  − Property Tax', value: '(' + fmt(effectiveAnnualTax) + ')', red: true },
              { label: '  − Insurance', value: '(' + fmt(effectiveAnnualIns) + ')', red: true },
              { label: '  − Maintenance', value: '(' + fmt(effectiveAnnualMaint) + ')', red: true },
              { label: 'Net Operating Income (NOI)', value: fmt(noi), bold: true, divider: true, color: noi >= 0 ? '#2d6a4f' : '#c0392b' },
              { label: '  − Annual Debt Service (' + (inputs.loanType === 'interestOnly' ? 'I/O' : 'P&I') + ')', value: '(' + fmt(monthlyPayment * 12) + ')', red: true },
              { label: 'Annual Cash Flow', value: fmt(noi - monthlyPayment * 12), bold: true, divider: true, color: (noi - monthlyPayment * 12) >= 0 ? '#2d6a4f' : '#c0392b' },
              { label: 'Monthly Cash Flow', value: fmt((noi - monthlyPayment * 12) / 12), bold: true, color: (noi - monthlyPayment * 12) >= 0 ? '#2d6a4f' : '#c0392b' },
            ].map((row, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: row.divider ? '2px solid #1a1a2e' : '1px solid #f5f5f5', marginTop: row.divider ? '4px' : '0' }}>
                <span style={{ fontSize: '13px', color: row.bold ? '#1a1a2e' : '#555', fontWeight: row.bold ? 'bold' : 'normal' }}>{row.label}</span>
                <span style={{ fontSize: '13px', fontWeight: row.bold ? 'bold' : 'normal', color: row.color || (row.red ? '#c0392b' : row.positive ? '#2d6a4f' : '#333') }}>{row.value}</span>
              </div>
            ))}
            <NextTabButton label="Next: Deal Scorecard" onClick={() => setActiveTab('scorecard')} />
          </div>
        )}
        {activeTab === 'scorecard' && (
          <div>
            <SectionHeader title="Deal Scorecard" />
            {inputs.propAddress && (
              <div style={{ backgroundColor: '#f0f8ff', padding: '10px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px', color: '#1a1a2e' }}>
                <strong>Property:</strong> {inputs.propAddress}{inputs.propCity ? `, ${inputs.propCity}` : ''}{inputs.propState ? `, ${inputs.propState}` : ''}
              </div>
            )}
            {(() => {
              const annualCarry = (parseFloat(inputs.carryTaxes)||0) + (parseFloat(inputs.carryInsurance)||0) + (parseFloat(inputs.carryHOA)||0)*12 + (parseFloat(inputs.carryUtilities)||0)*12 + (parseFloat(inputs.carryMaintenance)||0) + (parseFloat(inputs.carryOther)||0);
              return annualCarry > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: '12px', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={{ backgroundColor: '#c9a84c', padding: '10px 14px' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>Monthly Cost to Own</div>
                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: 'white' }}>{fmt(annualCarry/12)}</div>
                  </div>
                  <div style={{ backgroundColor: '#1a1a2e', padding: '10px 14px' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>Annual Cost to Own</div>
                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: 'white' }}>{fmt(annualCarry)}</div>
                  </div>
                </div>
              ) : null;
            })()}
            {/* Cash Outlay Section */}
            <div style={{ backgroundColor: '#f0f8ff', border: '1px solid #dde8f5', borderRadius: '8px', padding: '12px 14px', marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' }}>ðŸ’µ Actual Cash Out of Pocket</div>
              {[
                { label: 'Down Payment', value: fmt(downAmt) },
                { label: 'Closing Costs', value: fmt(n(inputs.closingCostsBuy) + n(inputs.inspectionFee) + n(inputs.miscBuy)) },
                { label: 'Rehab (w/ contingency)', value: fmt(rehabWithContingency) },
              ].map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', padding: '3px 0' }}>
                  <span>{r.label}</span><span>{r.value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', borderTop: '1px solid #dde8f5', marginTop: '6px', paddingTop: '6px' }}>
                <span>Total Cash Out of Pocket</span><span>{fmt(cashOutOfPocket)}</span>
              </div>
            </div>

            {[
              { label: 'Total All-In Cost (incl. loan)', value: fmt(totalAllIn), note: true },
              { label: 'Loan Amount', value: fmt(loanAmt) },
              { label: 'Monthly Payment (' + (inputs.loanType === 'interestOnly' ? 'I/O' : 'P&I') + ')', value: fmt(monthlyPayment) },
              { label: 'Net Profit (Flip)', value: fmt(netProfit), color: netProfit >= 0 ? '#2d6a4f' : '#c0392b' },
            ].map((item, i) => (
              <div key={i} style={{ backgroundColor: '#f8f8f8', padding: '10px 12px', borderRadius: '6px', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#555' }}>{item.label}</span>
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: item.color || '#333' }}>{item.value}</span>
              </div>
            ))}

            {/* Flip Returns */}
            <div style={{ marginTop: '12px', marginBottom: '6px', fontSize: '12px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Flip Returns</div>
            {[
              { label: 'ROI on Total Investment', value: fmtPct(roiSale), sub: 'Net profit Ã· total all-in cost', bg: scoreColor(roiSale, 20) },
              { label: 'Cash-on-Cash Return', value: fmtPct(trueCocSale), sub: 'Net profit Ã· cash out of pocket', bg: scoreColor(trueCocSale, 20) },
            ].map((item, i) => (
              <div key={i} style={{ backgroundColor: item.bg, color: 'white', padding: '12px 14px', borderRadius: '6px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.label}</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.75)', marginTop: '2px' }}>{item.sub}</div>
                </div>
                <span style={{ fontSize: '22px', fontWeight: 'bold' }}>{item.value}</span>
              </div>
            ))}

            {/* Hold Returns */}
            <div style={{ marginTop: '12px', marginBottom: '6px', fontSize: '12px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Hold / Rental Returns</div>
            {[
              { label: 'Annual NOI', value: fmt(noi), color: noi >= 0 ? '#2d6a4f' : '#c0392b' },
              { label: 'ROI on Total Investment', value: fmtPct(roiRent), sub: 'NOI Ã· total all-in cost', bg: scoreColor(roiRent, 14) },
              { label: 'Cash-on-Cash Return', value: fmtPct(cocRent), sub: 'NOI Ã· cash out of pocket', bg: scoreColor(cocRent, 14) },
            ].map((item, i) => (
              item.bg ? (
                <div key={i} style={{ backgroundColor: item.bg, color: 'white', padding: '12px 14px', borderRadius: '6px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.label}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.75)', marginTop: '2px' }}>{item.sub}</div>
                  </div>
                  <span style={{ fontSize: '22px', fontWeight: 'bold' }}>{item.value}</span>
                </div>
              ) : (
                <div key={i} style={{ backgroundColor: '#f8f8f8', padding: '10px 12px', borderRadius: '6px', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#555' }}>{item.label}</span>
                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: item.color || '#333' }}>{item.value}</span>
                </div>
              )
            ))}
            <SaveButton onSave={handleSave} saved={saved} />
          </div>
        )}
      </div>
    </div>
  );
}

// --- MULTI-FAMILY CALCULATOR ---
function MultiFamilyCalculator({ onSaveDeal }) {
  const [inputs, setInputs] = useState({
    propAddress: '', propCity: '', propState: '', propZip: '',
    propCounty: '', propParcel: '', propType: 'Multi-Family', propAuctionDate: '', propNotes: '',
    carryTaxesMo: '', carryTaxes: '', carryInsuranceMo: '', carryInsurance: '', carryHOAMo: '', carryHOA: '', carryUtilitiesMo: '', carryUtilities: '', carryMaintenanceMo: '', carryMaintenance: '', carryOtherMo: '', carryOther: '',
    purchasePrice: '', closingCosts: '', initialReserves: '',
    sqft: '', units: '', rentPerUnit: '', vacancyRate: '',
    downPct: '', loanType: 'amortizing', interestRate: '', termMonths: '',
    taxes: '', taxesMo: '', insurance: '', insuranceMo: '',
    water: '', waterMo: '', trash: '', trashMo: '',
    repairs: '', repairsMo: '', capex: '', capexMo: '',
    mgmtPct: '', threshold: '',
  });
  const [activeTab, setActiveTab] = useState('assumptions');
  const [saved, setSaved] = useState(false);
  const [showClosingModal, setShowClosingModal] = useState(false);
  const [sensitivityRentStep, setSensitivityRentStep] = useState('');
  const [sensitivityPriceStep, setSensitivityPriceStep] = useState('');
  const update = (field, value) => { setInputs(prev => ({ ...prev, [field]: value })); setSaved(false); };
  const n = (val) => parseFloat(val) || 0;

  const price = n(inputs.purchasePrice);
  const closing = n(inputs.closingCosts);
  const reserves = n(inputs.initialReserves);
  const units = n(inputs.units);
  const rentPerUnit = n(inputs.rentPerUnit);
  const vacancyPct = n(inputs.vacancyRate) / 100;
  const mgmtPct = n(inputs.mgmtPct) / 100;
  const threshold = n(inputs.threshold) / 100;
  const taxes = n(inputs.taxes);
  const insurance = n(inputs.insurance);
  const water = n(inputs.water);
  const trash = n(inputs.trash);
  const repairs = n(inputs.repairs);
  const capex = n(inputs.capex);

  const { downAmt, loanAmt, monthlyPayment } = calcFinancing(inputs.purchasePrice, inputs.downPct, inputs.interestRate, inputs.termMonths, inputs.loanType);
  const totalCash = downAmt + closing + reserves;
  const monthlyGrossRent = units * rentPerUnit;
  const annualGrossRent = monthlyGrossRent * 12;
  const egi = annualGrossRent * (1 - vacancyPct);
  const mgmtFee = egi * mgmtPct;
  const totalOpex = taxes + insurance + water + trash + repairs + capex + mgmtFee;
  const noi = egi - totalOpex;
  const annualDebt = monthlyPayment * 12;
  const annualCF = noi - annualDebt;
  const monthlyCF = annualCF / 12;
  const capRate = price > 0 ? noi / price : 0;
  const coc = totalCash > 0 ? annualCF / totalCash : 0;
  const dscr = annualDebt > 0 ? noi / annualDebt : 0;
  const expRatio = egi > 0 ? totalOpex / egi : 0;
  const pricePerSqft = n(inputs.sqft) > 0 ? price / n(inputs.sqft) : 0;

  const fmt = (num) => (num < 0 ? '-$' : '$') + Math.abs(Math.round(num)).toLocaleString();
  const fmtPct = (num) => (num * 100).toFixed(1) + '%';
  const scoreColor = (val, min) => val >= min ? '#2d6a4f' : val >= 0 ? '#c9a84c' : '#c0392b';

  const baseRent = rentPerUnit > 0 ? rentPerUnit : 1000;
  const autoRentStep = baseRent <= 800 ? 50 : baseRent <= 1500 ? 100 : baseRent <= 3000 ? 200 : 500;
  const rentStep = parseFloat(sensitivityRentStep) > 0 ? parseFloat(sensitivityRentStep) : autoRentStep;
  const rentVals = [-3,-2,-1,0,1,2,3].map(i => Math.round((baseRent + i * rentStep) / (rentStep || 1)) * (rentStep || 1)).filter(v => v > 0);

  const basePrice = price > 0 ? price : 400000;
  const autoPriceStep = basePrice <= 200000 ? 10000 : basePrice <= 500000 ? 25000 : basePrice <= 1000000 ? 50000 : 100000;
  const priceStep = parseFloat(sensitivityPriceStep) > 0 ? parseFloat(sensitivityPriceStep) : autoPriceStep;
  const priceVals = [-3,-2,-1,0,1,2,3].map(i => Math.round((basePrice + i * priceStep) / (priceStep || 1)) * (priceStep || 1)).filter(v => v > 0);

  const verdictBg = coc >= threshold ? '#e8f5e9' : coc >= 0 ? '#fff8e1' : '#ffebee';
  const verdictBorder = coc >= threshold ? '#2d6a4f' : coc >= 0 ? '#c9a84c' : '#c0392b';
  const verdictIcon = coc >= threshold ? 'PASS' : coc >= 0 ? 'WARN' : 'FAIL';
  const verdictTitle = coc >= threshold
    ? 'Deal passes — CoC of ' + fmtPct(coc) + ' meets your ' + fmtPct(threshold) + ' threshold'
    : coc >= 0
      ? 'Below threshold — CoC of ' + fmtPct(coc) + ', need ' + fmtPct(threshold) + ' to pass'
      : 'Negative cash flow — losing ' + fmt(Math.abs(monthlyCF)) + '/mo';

  const handleSave = () => {
    onSaveDeal({ type: 'Multi-Family', address: inputs.propAddress ? `${inputs.propAddress}${inputs.propCity ? ', '+inputs.propCity : ''}${inputs.propState ? ', '+inputs.propState : ''}` : '', inputs, metrics: { totalCash, monthlyCF, annualCF, noi, capRate, coc, dscr, monthlyPayment, downAmt, loanAmt, units } });
    setSaved(true);
  };

  const tabs = ['assumptions', 'returns', 'sensitivity', 'monthly'];
  const tabLabels = { assumptions: 'Acquisition Target', returns: 'Returns', sensitivity: 'Sensitivity', monthly: 'Monthly' };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
        {[
          { label: 'Total Cash In', value: fmt(totalCash) },
          { label: 'Monthly Cash Flow', value: fmt(monthlyCF), color: monthlyCF >= 0 ? '#2d6a4f' : '#c0392b' },
          { label: 'Cap Rate', value: fmtPct(capRate), bg: scoreColor(capRate, 0.06) },
          { label: 'Cash on Cash', value: fmtPct(coc), bg: scoreColor(coc, threshold) },
        ].map((item, i) => (
          <div key={i} style={{ backgroundColor: item.bg || '#f8f8f8', padding: '10px 12px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '11px', color: item.bg ? 'rgba(255,255,255,0.8)' : '#888' }}>{item.label}</span>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: item.bg ? 'white' : (item.color || '#333') }}>{item.value}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex: 1, padding: '8px 4px', borderRadius: '6px', border: 'none',
            backgroundColor: activeTab === tab ? '#1a1a2e' : '#f0f0f0',
            color: activeTab === tab ? 'white' : '#333',
            cursor: 'pointer', fontSize: '11px', fontWeight: activeTab === tab ? 'bold' : 'normal'
          }}>{tabLabels[tab]}</button>
        ))}
      </div>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {activeTab === 'assumptions' && (
          <div>
            <PropertyInfoSection inputs={inputs} update={update} />
            <SectionHeader title="Acquisition" />
            <InputRow label="Purchase Price" field="purchasePrice" inputs={inputs} update={update} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
              <label style={{ fontSize: '13px', color: '#555', flex: 1 }}>Closing Costs</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <button onClick={() => setShowClosingModal(true)} style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#c9a84c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Estimate →</button>
                <span style={{ fontSize: '13px', color: '#888' }}>$</span>
                <input type="text" inputMode="decimal" value={formatWithCommas(inputs.closingCosts || '')} onChange={e => update('closingCosts', stripCommas(e.target.value))} style={{ width: '90px', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', textAlign: 'right' }} />
              </div>
            </div>
            {showClosingModal && <ClosingCostModal purchasePrice={inputs.purchasePrice} state={inputs.propState || 'CA'} onApply={(val) => update('closingCosts', val)} onClose={() => setShowClosingModal(false)} />}
            <InputRow label="Initial Reserves / Repairs" field="initialReserves" inputs={inputs} update={update} />
            <InputRow label="Building Sq Ft" field="sqft" suffix="sqft" inputs={inputs} update={update} />
            <ReadOnlyRow label="Price per Sq Ft" value={'$' + pricePerSqft.toFixed(2)} />
            <SectionHeader title="Financing" />
            <InputRow label="Down Payment" field="downPct" suffix="%" inputs={inputs} update={update} />
            <LoanTypeToggle value={inputs.loanType} onChange={val => update('loanType', val)} />
            <InputRow label="Interest Rate" field="interestRate" suffix="%" inputs={inputs} update={update} />
            {inputs.loanType === 'amortizing' && <InputRow label="Term" field="termMonths" suffix="mo" inputs={inputs} update={update} />}
            <ReadOnlyRow label="Down Payment Amount" value={fmt(downAmt)} />
            <ReadOnlyRow label="Loan Amount" value={fmt(loanAmt)} />
            <ReadOnlyRow label={'Monthly Payment (' + (inputs.loanType === 'interestOnly' ? 'Interest Only' : 'P&I') + ')'} value={fmt(monthlyPayment)} bold color="#1a1a2e" />
            <ReadOnlyRow label="Total Cash Needed" value={fmt(totalCash)} bold />
            <SectionHeader title="Rent & Units" />
            <InputRow label="Number of Units" field="units" suffix="units" inputs={inputs} update={update} />
            <InputRow label="Rent Per Unit / Month" field="rentPerUnit" inputs={inputs} update={update} />
            <InputRow label="Vacancy Rate" field="vacancyRate" suffix="%" inputs={inputs} update={update} />
            <ReadOnlyRow label="Monthly Gross Rent" value={fmt(monthlyGrossRent)} />
            <ReadOnlyRow label="Effective Gross Income (annual)" value={fmt(egi)} />
            <SectionHeader title="Operating Expenses" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px', gap: '8px', padding: '4px 0 6px', borderBottom: '2px solid #1a1a2e' }}>
              <span style={{ fontSize: '11px', color: '#888', fontWeight: 'bold' }}>EXPENSE</span>
              <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#92400e', backgroundColor: '#fffbeb', borderRadius: '4px', padding: '3px' }}>MONTHLY</div>
              <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#1565c0', backgroundColor: '#f0f8ff', borderRadius: '4px', padding: '3px' }}>ANNUAL</div>
            </div>
            {[
              { label: 'Property Taxes',      mo: 'taxesMo',    yr: 'taxes' },
              { label: 'Insurance',           mo: 'insuranceMo', yr: 'insurance' },
              { label: 'Water / Sewer',       mo: 'waterMo',    yr: 'water' },
              { label: 'Trash',               mo: 'trashMo',    yr: 'trash' },
              { label: 'Repairs & Maintenance', mo: 'repairsMo', yr: 'repairs' },
              { label: 'CapEx Reserve',       mo: 'capexMo',    yr: 'capex' },
            ].map(r => (
              <DualCarryRow key={r.label} label={r.label} monthlyField={r.mo} annualField={r.yr} inputs={inputs} update={update} />
            ))}
            <InputRow label="Property Mgmt" field="mgmtPct" suffix="%" inputs={inputs} update={update} />
            {mgmtFee > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px', gap: '8px', padding: '4px 0' }}>
                <span style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', paddingLeft: '8px' }}>Mgmt Fee ({(mgmtPct*100).toFixed(1)}% of EGI)</span>
                <div style={{ textAlign: 'right', fontSize: '12px', color: '#555', paddingRight: '6px' }}>{fmt(mgmtFee/12)}</div>
                <div style={{ textAlign: 'right', fontSize: '12px', color: '#555', paddingRight: '6px' }}>{fmt(mgmtFee)}</div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px', gap: '4px', marginTop: '10px' }}>
              <div style={{ backgroundColor: '#1a1a2e', padding: '8px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'white', fontWeight: 'bold' }}>Total Operating Expenses</span>
              </div>
              <div style={{ backgroundColor: '#c9a84c', padding: '8px 6px', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)' }}>/ month</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>{fmt(totalOpex/12)}</div>
              </div>
              <div style={{ backgroundColor: '#1a1a2e', padding: '8px 6px', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)' }}>/ year</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>{fmt(totalOpex)}</div>
              </div>
            </div>
            <SectionHeader title="Your Return Threshold" />
            <InputRow label="Min. Cash-on-Cash Return" field="threshold" suffix="%" inputs={inputs} update={update} />
            <NextTabButton label="Next: Returns Analysis" onClick={() => setActiveTab('returns')} />
          </div>
        )}
        {activeTab === 'returns' && (
          <div>
            <div style={{ backgroundColor: verdictBg, border: '1px solid ' + verdictBorder, borderRadius: '6px', padding: '12px 14px', marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '13px', fontWeight: 'bold', backgroundColor: verdictBorder, color: 'white', padding: '2px 8px', borderRadius: '4px' }}>{verdictIcon}</span>
              <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#333' }}>{verdictTitle}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
              {[
                { label: 'Cash-on-Cash', value: fmtPct(coc), color: scoreColor(coc, threshold) },
                { label: 'Cap Rate', value: fmtPct(capRate), color: scoreColor(capRate, 0.06) },
                { label: 'DSCR', value: dscr.toFixed(2), color: dscr >= 1.25 ? '#2d6a4f' : dscr >= 1 ? '#c9a84c' : '#c0392b' },
                { label: 'NOI / yr', value: fmt(noi), color: noi > 0 ? '#2d6a4f' : '#c0392b' },
                { label: 'Cash Flow / mo', value: fmt(monthlyCF), color: monthlyCF > 0 ? '#2d6a4f' : '#c0392b' },
                { label: 'Expense Ratio', value: fmtPct(expRatio), color: expRatio < 0.5 ? '#2d6a4f' : expRatio < 0.65 ? '#c9a84c' : '#c0392b' },
              ].map((m, i) => (
                <div key={i} style={{ backgroundColor: '#f8f8f8', padding: '10px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>{m.label}</div>
                  <div style={{ fontSize: '15px', fontWeight: 'bold', color: m.color }}>{m.value}</div>
                </div>
              ))}
            </div>
            <SectionHeader title="Income" />
            {[
              { label: units + ' units x $' + rentPerUnit + '/mo x 12', value: fmt(annualGrossRent) },
              { label: 'Vacancy (' + fmtPct(vacancyPct) + ')', value: fmt(-(annualGrossRent - egi)) },
              { label: 'Effective Gross Income', value: fmt(egi), bold: true },
            ].map((r, i) => <ReadOnlyRow key={i} {...r} />)}
            <SectionHeader title="Operating Expenses" />
            {[
              { label: 'Property Taxes', value: fmt(taxes) },
              { label: 'Insurance', value: fmt(insurance) },
              { label: 'Water / Sewer', value: fmt(water) },
              { label: 'Trash', value: fmt(trash) },
              { label: 'Repairs & Maintenance', value: fmt(repairs) },
              { label: 'CapEx Reserve', value: fmt(capex) },
              { label: 'Property Mgmt (' + fmtPct(mgmtPct) + ')', value: fmt(mgmtFee) },
              { label: 'Total OpEx (' + fmtPct(expRatio) + ' of EGI)', value: fmt(totalOpex), bold: true },
            ].map((r, i) => <ReadOnlyRow key={i} {...r} />)}
            <SectionHeader title="Cash Flow" />
            {[
              { label: 'NOI', value: fmt(noi) },
              { label: 'Debt Service', value: fmt(-annualDebt) },
              { label: 'Annual Cash Flow', value: fmt(annualCF), bold: true, color: annualCF >= 0 ? '#2d6a4f' : '#c0392b' },
              { label: 'Monthly Cash Flow', value: fmt(monthlyCF), bold: true, color: monthlyCF >= 0 ? '#2d6a4f' : '#c0392b' },
            ].map((r, i) => <ReadOnlyRow key={i} {...r} />)}
            <SaveButton onSave={handleSave} saved={saved} />
            <NextTabButton label="Next: Sensitivity Table" onClick={() => setActiveTab('sensitivity')} />
          </div>
        )}
        {activeTab === 'sensitivity' && (
          <div>
            <div style={{ backgroundColor: '#f0f8ff', border: '1px solid #dde8f5', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 'bold', color: '#1a1a2e' }}>What is this table?</p>
              <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#555', lineHeight: '1.5' }}>
                This shows your <strong>Cash-on-Cash return</strong> at 7 different rent levels (rows) vs 7 different purchase prices (columns).
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', fontSize: '11px' }}>
                <span style={{ backgroundColor: '#e8f5e9', color: '#2d6a4f', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>Green = meets your threshold</span>
                <span style={{ backgroundColor: '#fff8e1', color: '#c9a84c', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>Amber = positive but below threshold</span>
                <span style={{ backgroundColor: '#ffebee', color: '#c0392b', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>Red = negative cash flow</span>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
              <div style={{ backgroundColor: '#f8f8f8', border: '1px solid #ddd', borderRadius: '6px', padding: '10px 12px' }}>
                <label style={{ fontSize: '11px', color: '#888', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>RENT STEP (per row)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#888' }}>$</span>
                  <input type="text" inputMode="numeric" value={formatWithCommas(sensitivityRentStep)} onChange={e => setSensitivityRentStep(stripCommas(e.target.value))} placeholder={'auto (' + autoRentStep + ')'} style={{ flex: 1, padding: '5px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', textAlign: 'right' }} />
                </div>
              </div>
              <div style={{ backgroundColor: '#f8f8f8', border: '1px solid #ddd', borderRadius: '6px', padding: '10px 12px' }}>
                <label style={{ fontSize: '11px', color: '#888', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>PRICE STEP (per column)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#888' }}>$</span>
                  <input type="text" inputMode="numeric" value={formatWithCommas(sensitivityPriceStep)} onChange={e => setSensitivityPriceStep(stripCommas(e.target.value))} placeholder={'auto (' + autoPriceStep.toLocaleString() + ')'} style={{ flex: 1, padding: '5px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', textAlign: 'right' }} />
                </div>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr>
                    <th style={{ backgroundColor: '#1a1a2e', color: 'white', padding: '6px 8px', textAlign: 'center' }}>Rent/unit</th>
                    {priceVals.map(p => (
                      <th key={p} style={{ backgroundColor: '#1a1a2e', color: 'white', padding: '6px 4px', textAlign: 'center', whiteSpace: 'nowrap' }}>${(p / 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}K</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rentVals.map(rent => (
                    <tr key={rent}>
                      <td style={{ backgroundColor: '#1a1a2e', color: 'white', padding: '6px 8px', textAlign: 'center', fontWeight: 'bold' }}>${rent.toLocaleString()}</td>
                      {priceVals.map(p => {
                        const { downAmt: da, monthlyPayment: mp } = calcFinancing(p, n(inputs.downPct), n(inputs.interestRate), n(inputs.termMonths), inputs.loanType);
                        const tc = da + closing + reserves;
                        const agr = units * rent * 12;
                        const e = agr * (1 - vacancyPct);
                        const mf2 = e * mgmtPct;
                        const opex = taxes + insurance + water + trash + repairs + capex + mf2;
                        const cf = (e - opex) - mp * 12;
                        const val = tc > 0 ? cf / tc : 0;
                        const isActive = rent === rentPerUnit && p === price;
                        const bg = val >= threshold ? '#e8f5e9' : val >= 0 ? '#fff8e1' : '#ffebee';
                        const color = val >= threshold ? '#2d6a4f' : val >= 0 ? '#c9a84c' : '#c0392b';
                        return (
                          <td key={p} style={{ backgroundColor: bg, color, padding: '6px 4px', textAlign: 'center', fontWeight: isActive ? 'bold' : 'normal', outline: isActive ? '2px solid #1a1a2e' : 'none', outlineOffset: '-2px' }}>
                            {(val * 100).toFixed(1)}%
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <NextTabButton label="Next: Monthly Breakdown" onClick={() => setActiveTab('monthly')} />
          </div>
        )}
        {activeTab === 'monthly' && (
          <div>
            <div style={{ fontSize: '12px', color: '#555', marginBottom: '12px' }}>Year 1 — flat rent, no escalation assumed.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
              {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map(mo => (
                <div key={mo} style={{ backgroundColor: '#f8f8f8', borderRadius: '6px', padding: '8px 10px' }}>
                  <div style={{ fontSize: '11px', color: '#888' }}>{mo}</div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: monthlyCF >= 0 ? '#2d6a4f' : '#c0392b' }}>{fmt(monthlyCF)}</div>
                </div>
              ))}
            </div>
            <SectionHeader title="Monthly Breakdown" />
            {[
              { label: 'Gross rent', value: fmt(monthlyGrossRent) },
              { label: 'Vacancy (' + fmtPct(vacancyPct) + ')', value: fmt(-(monthlyGrossRent - egi / 12)) },
              { label: 'Effective income', value: fmt(egi / 12), bold: true },
              { label: 'Property taxes', value: fmt(-taxes / 12) },
              { label: 'Insurance', value: fmt(-insurance / 12) },
              { label: 'Water / sewer', value: fmt(-water / 12) },
              { label: 'Trash', value: fmt(-trash / 12) },
              { label: 'Repairs & maintenance', value: fmt(-repairs / 12) },
              { label: 'CapEx reserve', value: fmt(-capex / 12) },
              { label: 'Property management', value: fmt(-mgmtFee / 12) },
              { label: 'Debt service (' + (inputs.loanType === 'interestOnly' ? 'Interest Only' : 'P&I') + ')', value: fmt(-monthlyPayment) },
              { label: 'Net cash flow', value: fmt(monthlyCF), bold: true, color: monthlyCF >= 0 ? '#2d6a4f' : '#c0392b' },
            ].map((r, i) => <ReadOnlyRow key={i} {...r} />)}
            <SaveButton onSave={handleSave} saved={saved} />
          </div>
        )}
      </div>
    </div>
  );
}

// --- TAX DEED CALCULATOR ---
function TaxDeedCalculator({ onSaveDeal }) {
  const [inputs, setInputs] = useState({
    propAddress: '', propCity: '', propState: '', propZip: '',
    propCounty: '', propParcel: '', propType: '', propAuctionDate: '', propNotes: '',
    carryTaxesMo: '', carryTaxes: '', carryInsuranceMo: '', carryInsurance: '', carryHOAMo: '', carryHOA: '', carryUtilitiesMo: '', carryUtilities: '', carryMaintenanceMo: '', carryMaintenance: '', carryOtherMo: '', carryOther: '',
    winningBid: '', backTaxes: '', titleSearch: '',
    quietTitle: '', recordingFees: '', miscClosing: '',
    totalRehab: '',
    financingEnabled: false,
    downPct: '', interestRate: '', termMonths: '', loanType: 'amortizing',
    arv: '', agentCommission: '', sellerClosing: '',
    holdingMonths: '', monthlyHolding: '', otherSaleExp: '',
    monthlyRent: '', vacancyRate: '', mgmtFee: '',
    annualTax: '', annualInsurance: '', annualMaintenance: '',
  });
  const [activeTab, setActiveTab] = useState('acquisition');
  const [saved, setSaved] = useState(false);
  const [showClosingModal, setShowClosingModal] = useState(false);
  const update = (field, value) => { setInputs(prev => ({ ...prev, [field]: value })); setSaved(false); };
  const n = (val) => parseFloat(val) || 0;

  const totalAcq = n(inputs.winningBid) + n(inputs.backTaxes) + n(inputs.titleSearch) +
    n(inputs.quietTitle) + n(inputs.recordingFees) + n(inputs.miscClosing);
  const totalAllIn = totalAcq + n(inputs.totalRehab);

  const financing = inputs.financingEnabled
    ? calcFinancing(totalAllIn, inputs.downPct, inputs.interestRate, inputs.termMonths, inputs.loanType)
    : { downAmt: totalAllIn, loanAmt: 0, monthlyPayment: 0 };
  const { downAmt, loanAmt, monthlyPayment } = financing;
  const totalCashNeeded = inputs.financingEnabled ? downAmt : totalAllIn;

  const carryExpensesMonthly = (n(inputs.carryTaxes) + n(inputs.carryInsurance) + n(inputs.carryHOA) + n(inputs.carryUtilities) + n(inputs.carryMaintenance) + n(inputs.carryOther)) / 12;
  const autoMonthlyHolding = carryExpensesMonthly + monthlyPayment;
  const effectiveMonthlyHolding = n(inputs.monthlyHolding) > 0 ? n(inputs.monthlyHolding) : autoMonthlyHolding;
  const agentDollar = n(inputs.arv) * (n(inputs.agentCommission) / 100);
  const closingDollar = n(inputs.arv) * (n(inputs.sellerClosing) / 100);
  const holdingTotal = n(inputs.holdingMonths) * effectiveMonthlyHolding;
  const totalSaleExp = agentDollar + closingDollar + holdingTotal + n(inputs.otherSaleExp);
  const netProceeds = n(inputs.arv) - totalSaleExp;
  const netProfit = netProceeds - totalAllIn;
  const cocSale = totalAllIn > 0 ? (netProfit / totalAllIn) * 100 : 0;
  const grossRent = n(inputs.monthlyRent) * 12;
  const vacancy = grossRent * (n(inputs.vacancyRate) / 100);
  const mgmtDollar = grossRent * (n(inputs.mgmtFee) / 100);
  const effectiveAnnualTax = n(inputs.annualTax) > 0 ? n(inputs.annualTax) : n(inputs.carryTaxes);
  const effectiveAnnualIns = n(inputs.annualInsurance) > 0 ? n(inputs.annualInsurance) : n(inputs.carryInsurance);
  const effectiveAnnualMaint = n(inputs.annualMaintenance) > 0 ? n(inputs.annualMaintenance) : n(inputs.carryMaintenance);
  const totalRentExp = mgmtDollar + vacancy + effectiveAnnualTax + effectiveAnnualIns + effectiveAnnualMaint + (monthlyPayment * 12);
  const noi = grossRent - totalRentExp;
  const cocRent = totalAllIn > 0 ? (noi / totalAllIn) * 100 : 0;

  const fmt = (num) => (num < 0 ? '-$' : '$') + Math.abs(Math.round(num)).toLocaleString();
  const fmtPct = (num) => num.toFixed(1) + '%';
  const scoreColor = (pct, min) => pct >= min ? '#2d6a4f' : pct >= min * 0.75 ? '#c9a84c' : '#c0392b';
  const tabs = ['acquisition', 'sale', 'rental', 'scorecard'];
  const tabLabels = { acquisition: 'Acquisition Target', sale: 'Sale', rental: 'Rental', scorecard: 'Score' };

  const handleSave = () => {
    onSaveDeal({ type: 'Tax Deed', address: inputs.propAddress ? `${inputs.propAddress}${inputs.propCity ? ', '+inputs.propCity : ''}${inputs.propState ? ', '+inputs.propState : ''}` : '', inputs, metrics: { totalAllIn, totalCashNeeded, netProfit, cocSale, noi, cocRent, monthlyPayment, downAmt, loanAmt } });
    setSaved(true);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
        {[
          { label: 'All-In Cost', value: fmt(totalAllIn) },
          { label: 'Cash Needed', value: fmt(totalCashNeeded) },
          { label: 'CoC Sale', value: fmtPct(cocSale), bg: scoreColor(cocSale, 20) },
          { label: 'CoC Rental', value: fmtPct(cocRent), bg: scoreColor(cocRent, 14) },
        ].map((item, i) => (
          <div key={i} style={{ backgroundColor: item.bg || '#f8f8f8', padding: '10px 12px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '11px', color: item.bg ? 'rgba(255,255,255,0.8)' : '#888' }}>{item.label}</span>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: item.bg ? 'white' : (item.color || '#333') }}>{item.value}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex: 1, padding: '8px 4px', borderRadius: '6px', border: 'none',
            backgroundColor: activeTab === tab ? '#1a1a2e' : '#f0f0f0',
            color: activeTab === tab ? 'white' : '#333',
            cursor: 'pointer', fontSize: '11px', fontWeight: activeTab === tab ? 'bold' : 'normal'
          }}>{tabLabels[tab]}</button>
        ))}
      </div>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {activeTab === 'acquisition' && (
          <div>
            <PropertyInfoSection inputs={inputs} update={update} />
            <SectionHeader title="Acquisition Costs" />
            <InputRow label="Winning Bid" field="winningBid" inputs={inputs} update={update} />
            <InputRow label="Back Taxes / Liens" field="backTaxes" inputs={inputs} update={update} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
              <label style={{ fontSize: '13px', color: '#555', flex: 1 }}>Title Search</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <button onClick={() => setShowClosingModal(true)} style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#c9a84c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Estimate →</button>
                <span style={{ fontSize: '13px', color: '#888' }}>$</span>
                <input type="text" inputMode="decimal" value={formatWithCommas(inputs.titleSearch || '')} onChange={e => update('titleSearch', stripCommas(e.target.value))} style={{ width: '90px', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', textAlign: 'right' }} />
              </div>
            </div>
            {showClosingModal && <ClosingCostModal purchasePrice={inputs.winningBid} state={inputs.propState || 'CA'} onApply={(val) => update('titleSearch', val)} onClose={() => setShowClosingModal(false)} />}
            <InputRow label="Quiet Title / Attorney" field="quietTitle" inputs={inputs} update={update} />
            <InputRow label="Recording Fees" field="recordingFees" inputs={inputs} update={update} />
            <InputRow label="Misc Closing" field="miscClosing" inputs={inputs} update={update} />
            <ReadOnlyRow label="Total Acquisition" value={fmt(totalAcq)} bold />
            <SectionHeader title="Rehab / CapEx" />
            <InputRow label="Total Rehab Estimate" field="totalRehab" inputs={inputs} update={update} />
            <div style={{ backgroundColor: '#1a1a2e', color: 'white', padding: '12px', borderRadius: '6px', marginTop: '12px', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
              <span>Total All-In Cost</span><span>{fmt(totalAllIn)}</span>
            </div>
            <SectionHeader title="Financing" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ fontSize: '13px', color: '#555' }}>Use Financing?</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: inputs.financingEnabled ? '#888' : '#1a1a2e', fontWeight: inputs.financingEnabled ? 'normal' : 'bold' }}>All Cash</span>
                <div onClick={() => update('financingEnabled', !inputs.financingEnabled)} style={{ width: '44px', height: '24px', borderRadius: '12px', cursor: 'pointer', position: 'relative', backgroundColor: inputs.financingEnabled ? '#2d6a4f' : '#ccc', transition: 'background 0.2s' }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'white', position: 'absolute', top: '3px', transition: 'left 0.2s', left: inputs.financingEnabled ? '23px' : '3px' }} />
                </div>
                <span style={{ fontSize: '12px', color: inputs.financingEnabled ? '#1a1a2e' : '#888', fontWeight: inputs.financingEnabled ? 'bold' : 'normal' }}>Financed</span>
              </div>
            </div>
            {!inputs.financingEnabled && (
              <div style={{ backgroundColor: '#f0f8ff', padding: '10px 12px', borderRadius: '6px', marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#555' }}>Total Cash Required (All-In)</span>
                <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e' }}>{fmt(totalAllIn)}</span>
              </div>
            )}
            {inputs.financingEnabled && (
              <div>
                <div style={{ backgroundColor: '#fff8e1', border: '1px solid #f0c040', padding: '8px 12px', borderRadius: '6px', marginTop: '8px', fontSize: '12px', color: '#7a5800' }}>
                  Note: Tax deed auctions typically require full payment within 24-72 hrs. Financing must be pre-arranged.
                </div>
                <InputRow label="Down Payment" field="downPct" suffix="%" inputs={inputs} update={update} />
                <LoanTypeToggle value={inputs.loanType} onChange={val => update('loanType', val)} />
                <InputRow label="Interest Rate" field="interestRate" suffix="%" inputs={inputs} update={update} />
                {inputs.loanType === 'amortizing' && <InputRow label="Term" field="termMonths" suffix="mo" inputs={inputs} update={update} />}
                <ReadOnlyRow label="Down Payment Amount" value={fmt(downAmt)} />
                <ReadOnlyRow label="Loan Amount" value={fmt(loanAmt)} />
                <ReadOnlyRow label="Monthly Payment" value={fmt(monthlyPayment)} bold color="#1a1a2e" />
                <div style={{ backgroundColor: '#f0f8ff', padding: '10px 12px', borderRadius: '6px', marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#555' }}>Total Cash Required (Down + Costs)</span>
                  <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e' }}>{fmt(totalCashNeeded)}</span>
                </div>
              </div>
            )}
            <CarryingCostsSection inputs={inputs} update={update} monthlyPayment={monthlyPayment} />
            <NextTabButton label="Next: Sale Scenario" onClick={() => setActiveTab('sale')} />
          </div>
        )}
        {activeTab === 'sale' && (
          <div>
            <SectionHeader title="Sale Scenario" />
            <InputRow label="Estimated ARV" field="arv" inputs={inputs} update={update} />
            <InputRow label="Agent Commission" field="agentCommission" suffix="%" inputs={inputs} update={update} />
            <InputRow label="Seller Closing Costs" field="sellerClosing" suffix="%" inputs={inputs} update={update} />
            <InputRow label="Holding Period" field="holdingMonths" suffix="mo" inputs={inputs} update={update} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
              <label style={{ fontSize: '13px', color: '#555', flex: 1 }}>Monthly Holding Cost</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {n(inputs.monthlyHolding) === 0 && autoMonthlyHolding > 0 && (
                  <span style={{ fontSize: '11px', color: '#c9a84c', fontStyle: 'italic' }}>auto from carry costs</span>
                )}
                <span style={{ fontSize: '13px', color: '#888' }}>$</span>
                <input type="text" inputMode="decimal" value={formatWithCommas(inputs.monthlyHolding || '')} onChange={e => update('monthlyHolding', stripCommas(e.target.value))} placeholder={autoMonthlyHolding > 0 ? Math.round(autoMonthlyHolding).toLocaleString() : ''} style={{ width: '90px', padding: '4px 8px', border: '1px solid ' + (n(inputs.monthlyHolding) === 0 && autoMonthlyHolding > 0 ? '#c9a84c' : '#ddd'), borderRadius: '4px', fontSize: '16px', textAlign: 'right', backgroundColor: n(inputs.monthlyHolding) === 0 && autoMonthlyHolding > 0 ? '#fffbeb' : 'white' }} />
              </div>
            </div>
            <InputRow label="Other Sale Expenses" field="otherSaleExp" inputs={inputs} update={update} />
            <ReadOnlyRow label="Net Proceeds" value={fmt(netProceeds)} />
            <ReadOnlyRow label="Net Profit" value={fmt(netProfit)} bold color={netProfit >= 0 ? '#2d6a4f' : '#c0392b'} />
            <NextTabButton label="Next: Rental Analysis" onClick={() => setActiveTab('rental')} />
          </div>
        )}
        {activeTab === 'rental' && (
          <div>
            <SectionHeader title="Rental Scenario" />
            <InputRow label="Monthly Gross Rent" field="monthlyRent" inputs={inputs} update={update} />
            <InputRow label="Vacancy Rate" field="vacancyRate" suffix="%" inputs={inputs} update={update} />
            <InputRow label="Mgmt Fee" field="mgmtFee" suffix="%" inputs={inputs} update={update} />
            {[
              { label: 'Annual Property Tax', field: 'annualTax', carry: 'carryTaxes' },
              { label: 'Annual Insurance', field: 'annualInsurance', carry: 'carryInsurance' },
              { label: 'Annual Maintenance', field: 'annualMaintenance', carry: 'carryMaintenance' },
            ].map(({ label, field, carry }) => {
              const autoVal = n(inputs[carry]);
              const isAuto = !inputs[field] && autoVal > 0;
              return (
                <div key={field} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '13px', color: '#555' }}>{label}</label>
                    {isAuto && <span style={{ fontSize: '11px', color: '#2d6a4f', marginLeft: '6px', fontStyle: 'italic' }}>auto from carry costs</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    {inputs[field] && autoVal > 0 && (
                      <button onClick={() => update(field, '')} style={{ fontSize: '10px', color: '#c9a84c', background: 'none', border: '1px solid #c9a84c', borderRadius: '3px', padding: '1px 5px', cursor: 'pointer' }}>auto</button>
                    )}
                    <span style={{ fontSize: '13px', color: '#888' }}>$</span>
                    <input type="text" inputMode="decimal"
                      value={formatWithCommas(inputs[field] || '')}
                      placeholder={autoVal > 0 ? Math.round(autoVal).toLocaleString() : '0'}
                      onChange={e => update(field, stripCommas(e.target.value))}
                      style={{ width: '90px', padding: '4px 8px', border: '1px solid ' + (inputs[field] ? '#c9a84c' : '#ddd'), borderRadius: '4px', fontSize: '16px', textAlign: 'right', backgroundColor: inputs[field] ? '#fffbeb' : 'white' }} />
                  </div>
                </div>
              );
            })}
            {inputs.financingEnabled && <ReadOnlyRow label="Monthly Financing (P&I)" value={fmt(monthlyPayment)} />}
            <ReadOnlyRow label="Annual NOI" value={fmt(noi)} bold color={noi >= 0 ? '#2d6a4f' : '#c0392b'} />
            <NextTabButton label="Next: Deal Scorecard" onClick={() => setActiveTab('scorecard')} />
          </div>
        )}
        {activeTab === 'scorecard' && (
          <div>
            <SectionHeader title="Deal Scorecard" />
            {inputs.propAddress && (
              <div style={{ backgroundColor: '#f0f8ff', padding: '10px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px', color: '#1a1a2e' }}>
                <strong>Property:</strong> {inputs.propAddress}{inputs.propCity ? `, ${inputs.propCity}` : ''}{inputs.propState ? `, ${inputs.propState}` : ''}
              </div>
            )}
            {[
              { label: 'Total All-In Cost', value: fmt(totalAllIn) },
              { label: 'Cash Required', value: fmt(totalCashNeeded) },
              inputs.financingEnabled ? { label: 'Monthly Payment', value: fmt(monthlyPayment) } : null,
              { label: 'Net Profit (Sale)', value: fmt(netProfit), color: netProfit >= 0 ? '#2d6a4f' : '#c0392b' },
              { label: 'CoC Return (Sale)', value: fmtPct(cocSale), bg: scoreColor(cocSale, 20) },
              { label: 'Annual NOI (Rental)', value: fmt(noi) },
              { label: 'CoC Return (Rental)', value: fmtPct(cocRent), bg: scoreColor(cocRent, 14) },
            ].filter(Boolean).map((item, i) => (
              <div key={i} style={{ backgroundColor: item.bg || '#f8f8f8', color: item.bg ? 'white' : '#333', padding: '12px', borderRadius: '6px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px' }}>{item.label}</span>
                <span style={{ fontSize: '16px', fontWeight: 'bold', color: item.bg ? 'white' : (item.color || 'inherit') }}>{item.value}</span>
              </div>
            ))}
            <SaveButton onSave={handleSave} saved={saved} />
          </div>
        )}
      </div>
    </div>
  );
}

// --- TAX LIEN CALCULATOR ---
function TaxLienCalculator({ onSaveDeal }) {
  const [inputs, setInputs] = useState({
    propAddress: '', propCity: '', propState: '', propZip: '',
    propCounty: '', propParcel: '', propType: '', propAuctionDate: '', propNotes: '',
    carryTaxesMo: '', carryTaxes: '', carryInsuranceMo: '', carryInsurance: '', carryHOAMo: '', carryHOA: '', carryUtilitiesMo: '', carryUtilities: '', carryMaintenanceMo: '', carryMaintenance: '', carryOtherMo: '', carryOther: '',
    lienAmount: '', penaltyRate: '', interestRate: '', state: 'FL',
    redemptionMonths: '', purchasePremium: '',
    investmentCash: '', otherCosts: '',
  });
  const [activeTab, setActiveTab] = useState('inputs');
  const [saved, setSaved] = useState(false);
  const update = (field, value) => { setInputs(prev => ({ ...prev, [field]: value })); setSaved(false); };
  const n = (val) => parseFloat(val) || 0;

  const lienAmt = n(inputs.lienAmount);
  const premium = n(inputs.purchasePremium);
  const totalInvested = lienAmt + premium + n(inputs.otherCosts);
  const penaltyRate = n(inputs.penaltyRate) / 100;
  const annualRate = n(inputs.interestRate) / 100;
  const months = n(inputs.redemptionMonths);

  const penaltyEarned = lienAmt * penaltyRate;
  const interestEarned = lienAmt * annualRate * (months / 12);
  const totalReturn = penaltyEarned + interestEarned;
  const totalReceived = lienAmt + totalReturn;
  const netProfit = totalReceived - totalInvested;
  const cocReturn = totalInvested > 0 ? (netProfit / totalInvested) * 100 : 0;
  const annualizedReturn = months > 0 ? (cocReturn / months) * 12 : 0;

  const fmt = (num) => (num < 0 ? '-$' : '$') + Math.abs(Math.round(num)).toLocaleString();
  const fmtPct = (num) => num.toFixed(1) + '%';
  const scoreColor = (pct, min) => pct >= min ? '#2d6a4f' : pct >= min * 0.75 ? '#c9a84c' : '#c0392b';

  const statePresets = {
    FL: { penaltyRate: '5', interestRate: '18', label: 'Florida — 18% / 5% min penalty' },
    GA: { penaltyRate: '20', interestRate: '0', label: 'Georgia — 20% flat penalty' },
    MD: { penaltyRate: '6', interestRate: '6', label: 'Maryland — 6-24% interest' },
    IN: { penaltyRate: '15', interestRate: '0', label: 'Indiana — 15% penalty' },
    IL: { penaltyRate: '0', interestRate: '36', label: 'Illinois — up to 36% (bid down)' },
    AZ: { penaltyRate: '0', interestRate: '16', label: 'Arizona — up to 16% (bid down)' },
    TX: { penaltyRate: '25', interestRate: '0', label: 'Texas — 25% penalty (non-homestead)' },
  };

  const applyPreset = (stateCode) => {
    const p = statePresets[stateCode];
    if (p) setInputs(prev => ({ ...prev, state: stateCode, penaltyRate: p.penaltyRate, interestRate: p.interestRate }));
  };

  const handleSave = () => {
    onSaveDeal({ type: 'Tax Lien', address: inputs.propAddress ? `${inputs.propAddress}${inputs.propCity ? ', '+inputs.propCity : ''}${inputs.propState ? ', '+inputs.propState : ''}` : '', inputs, metrics: { totalInvested, totalReturn, netProfit, cocReturn, annualizedReturn, penaltyEarned, interestEarned } });
    setSaved(true);
  };

  const tabs = ['inputs', 'returns', 'scorecard'];
  const tabLabels = { inputs: 'Acquisition Target', returns: 'Returns', scorecard: 'Score' };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
        {[
          { label: 'Total Invested', value: fmt(totalInvested) },
          { label: 'Total Return', value: fmt(totalReturn), color: totalReturn >= 0 ? '#2d6a4f' : '#c0392b' },
          { label: 'CoC Return', value: fmtPct(cocReturn), bg: scoreColor(cocReturn, 14) },
          { label: 'Annualized', value: fmtPct(annualizedReturn), bg: scoreColor(annualizedReturn, 14) },
        ].map((item, i) => (
          <div key={i} style={{ backgroundColor: item.bg || '#f8f8f8', padding: '10px 12px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '11px', color: item.bg ? 'rgba(255,255,255,0.8)' : '#888' }}>{item.label}</span>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: item.bg ? 'white' : (item.color || '#333') }}>{item.value}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex: 1, padding: '8px 4px', borderRadius: '6px', border: 'none',
            backgroundColor: activeTab === tab ? '#1a1a2e' : '#f0f0f0',
            color: activeTab === tab ? 'white' : '#333',
            cursor: 'pointer', fontSize: '12px', fontWeight: activeTab === tab ? 'bold' : 'normal'
          }}>{tabLabels[tab]}</button>
        ))}
      </div>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {activeTab === 'inputs' && (
          <div>
            <PropertyInfoSection inputs={inputs} update={update} />
            <SectionHeader title="State Presets" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              {Object.entries(statePresets).map(([code, preset]) => (
                <button key={code} onClick={() => applyPreset(code)} style={{
                  padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer', fontSize: '12px',
                  backgroundColor: inputs.state === code ? '#1a1a2e' : 'white',
                  color: inputs.state === code ? 'white' : '#333',
                  fontWeight: inputs.state === code ? 'bold' : 'normal',
                }}>{code}</button>
              ))}
            </div>
            {statePresets[inputs.state] && (
              <div style={{ backgroundColor: '#f0f8ff', padding: '8px 12px', borderRadius: '6px', marginBottom: '8px', fontSize: '12px', color: '#1565c0' }}>
                {statePresets[inputs.state].label}
              </div>
            )}
            <SectionHeader title="Lien Details" />
            <InputRow label="Lien Amount" field="lienAmount" inputs={inputs} update={update} />
            <InputRow label="Purchase Premium (overbid)" field="purchasePremium" inputs={inputs} update={update} />
            <InputRow label="Other Costs (research, filing)" field="otherCosts" inputs={inputs} update={update} />
            <ReadOnlyRow label="Total Invested" value={fmt(totalInvested)} bold />
            <SectionHeader title="Rate Structure" />
            <InputRow label="Penalty Rate (one-time)" field="penaltyRate" suffix="%" inputs={inputs} update={update} />
            <InputRow label="Annual Interest Rate" field="interestRate" suffix="%" inputs={inputs} update={update} />
            <InputRow label="Expected Redemption Period" field="redemptionMonths" suffix="mo" inputs={inputs} update={update} />
            <CarryingCostsSection inputs={inputs} update={update} monthlyPayment={0} />
            <NextTabButton label="Next: Returns" onClick={() => setActiveTab('returns')} />
          </div>
        )}
        {activeTab === 'returns' && (
          <div>
            <SectionHeader title="Return Breakdown" />
            {[
              { label: 'Lien Amount', value: fmt(lienAmt) },
              { label: 'Penalty Earned (' + fmtPct(penaltyRate) + ' one-time)', value: fmt(penaltyEarned) },
              { label: 'Interest Earned (' + fmtPct(annualRate) + '/yr x ' + months + ' mo)', value: fmt(interestEarned) },
              { label: 'Total Return', value: fmt(totalReturn), bold: true, color: '#2d6a4f' },
              { label: 'Total Received at Redemption', value: fmt(totalReceived), bold: true },
            ].map((r, i) => <ReadOnlyRow key={i} {...r} />)}
            <SectionHeader title="Performance" />
            {[
              { label: 'Total Invested', value: fmt(totalInvested) },
              { label: 'Net Profit', value: fmt(netProfit), color: netProfit >= 0 ? '#2d6a4f' : '#c0392b' },
              { label: 'Cash-on-Cash Return', value: fmtPct(cocReturn), bold: true, color: scoreColor(cocReturn, 14) },
              { label: 'Annualized Return', value: fmtPct(annualizedReturn), bold: true, color: scoreColor(annualizedReturn, 14) },
            ].map((r, i) => <ReadOnlyRow key={i} {...r} />)}
            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fff8e1', borderRadius: '6px', fontSize: '12px', color: '#7a5800' }}>
              <strong>If not redeemed:</strong> you may proceed to take ownership of the property through a tax deed application.
            </div>
            <NextTabButton label="Next: Scorecard" onClick={() => setActiveTab('scorecard')} />
          </div>
        )}
        {activeTab === 'scorecard' && (
          <div>
            <SectionHeader title="Tax Lien Scorecard" />
            {inputs.propAddress && (
              <div style={{ backgroundColor: '#f0f8ff', padding: '10px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px', color: '#1a1a2e' }}>
                <strong>Property:</strong> {inputs.propAddress}{inputs.propCity ? `, ${inputs.propCity}` : ''}{inputs.propState ? `, ${inputs.propState}` : ''}
              </div>
            )}
            {[
              { label: 'Total Invested', value: fmt(totalInvested) },
              { label: 'Penalty Earned', value: fmt(penaltyEarned) },
              { label: 'Interest Earned', value: fmt(interestEarned) },
              { label: 'Net Profit', value: fmt(netProfit), color: netProfit >= 0 ? '#2d6a4f' : '#c0392b' },
              { label: 'Cash-on-Cash Return', value: fmtPct(cocReturn), bg: scoreColor(cocReturn, 14) },
              { label: 'Annualized Return', value: fmtPct(annualizedReturn), bg: scoreColor(annualizedReturn, 14) },
              { label: 'Redemption Period', value: months + ' months' },
            ].map((item, i) => (
              <div key={i} style={{ backgroundColor: item.bg || '#f8f8f8', color: item.bg ? 'white' : '#333', padding: '12px', borderRadius: '6px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px' }}>{item.label}</span>
                <span style={{ fontSize: '16px', fontWeight: 'bold', color: item.bg ? 'white' : (item.color || 'inherit') }}>{item.value}</span>
              </div>
            ))}
            <SaveButton onSave={handleSave} saved={saved} />
          </div>
        )}
      </div>
    </div>
  );
}

// --- MAIN WRAPPER ---
function DealCalculator({ onSaveDeal }) {
  const [calcType, setCalcType] = useState('fixflip');
  const types = [
    { id: 'fixflip', label: 'Fix & Flip / Hold', icon: '🔨' },
    { id: 'multifamily', label: 'Multi-Family', icon: '🏢' },
    { id: 'taxdeed', label: 'Tax Deed', icon: '🏛️' },
    { id: 'taxlien', label: 'Tax Lien', icon: '📜' },
  ];
  return (
    <div>
      <h2 style={{ marginBottom: '16px', fontSize: '20px' }}>Deal Calculator</h2>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', overflowX: 'auto', paddingBottom: '4px' }}>
        {types.map(type => (
          <button key={type.id} onClick={() => setCalcType(type.id)} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '10px 16px', borderRadius: '8px', border: 'none',
            backgroundColor: calcType === type.id ? '#1a1a2e' : '#f0f0f0',
            color: calcType === type.id ? 'white' : '#333',
            cursor: 'pointer', flexShrink: 0, gap: '4px'
          }}>
            <span style={{ fontSize: '20px' }}>{type.icon}</span>
            <span style={{ fontSize: '12px', fontWeight: calcType === type.id ? 'bold' : 'normal' }}>{type.label}</span>
          </button>
        ))}
      </div>
      {calcType === 'fixflip' && <FixFlipCalculator onSaveDeal={onSaveDeal} />}
      {calcType === 'multifamily' && <MultiFamilyCalculator onSaveDeal={onSaveDeal} />}
      {calcType === 'taxdeed' && <TaxDeedCalculator onSaveDeal={onSaveDeal} />}
      {calcType === 'taxlien' && <TaxLienCalculator onSaveDeal={onSaveDeal} />}
    </div>
  );
}

export default DealCalculator;
